import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
  Search,
  ArrowRight,
  Layers,
  GraduationCap,
  Users,
  CalendarCheck,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Plus,
} from 'lucide-react';
import { BatchFormDialog } from '@/features/OrgLayout/management/BatchFormDialog';

import { EmptyState } from '@/components/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { useOrgBatches, useOrgOverview } from '@/lib/hooks/useOrg';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { OrgStatusBadge } from '@/features/OrgLayout/primitives/OrgStatusBadge';
import { OrgProgressBar } from '@/features/OrgLayout/primitives/OrgProgressBar';
import { OrgStatTile } from '@/features/OrgLayout/primitives/OrgStatTile';
import type { OrgBatchSummary } from '@/lib/api/org';

export const Route = createFileRoute('/org/batches/')({
  component: BatchesPage,
});

const PAGE_SIZE = 8;
const SELECT_TRIGGER_CLASS =
  'h-[42px] w-auto min-w-[140px] rounded-[14px] border-[#ececec] bg-white px-[17px] text-sm font-medium text-[#6b7280] focus:ring-0 focus:ring-offset-0';

function BatchesPage() {
  const { data, isLoading, isError, refetch } = useOrgBatches();
  const { data: overview } = useOrgOverview();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [trainerFilter, setTrainerFilter] = useState('all');
  const [page, setPage] = useState(1);

  const trainerOptions = useMemo(() => {
    const names = new Set<string>();
    (data ?? []).forEach((b) => b.trainers.forEach((t) => names.add(t.name)));
    return Array.from(names).sort();
  }, [data]);

  const filtered = useMemo(() => {
    return (data ?? []).filter((b) => {
      if (search.trim() && !b.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (trainerFilter !== 'all' && !b.trainers.some((t) => t.name === trainerFilter)) return false;
      return true;
    });
  }, [data, search, statusFilter, trainerFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const from = filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const open = (batchId: string) => navigate({ to: '/org/batches/$batchId', params: { batchId } });

  const resetPage = () => setPage(1);
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="w-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[42px] font-bold leading-[46.2px] text-[#111827]">Batches</h1>
          <p className="pt-2 text-base text-[#6b7280]">
            Manage and view all learning batches in your organization.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="mt-2 inline-flex flex-shrink-0 items-center gap-2 rounded-[14px] bg-[#ff5a1f] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#e64f18]"
        >
          <Plus className="h-4 w-4" /> New batch
        </button>
      </div>
      <BatchFormDialog
        mode="create"
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={(id) => open(id)}
      />

      {isLoading ? (
        <div className="space-y-2 pt-7">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-[24px]" />
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center py-16 text-center">
          <p className="text-muted-foreground">Couldn&apos;t load batches.</p>
          <Button className="mt-4" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <div className="pt-7">
          <EmptyState icon={Layers} title="No batches yet" description="Batches created for your organization will appear here." />
        </div>
      ) : (
        <>
          {/* Filter row */}
          <div className="flex items-center justify-between pt-7">
            <div className="flex h-[42px] w-[320px] items-center gap-3 rounded-[14px] border border-[#ececec] bg-white px-[17px]">
              <Search className="h-[15px] w-[15px] flex-shrink-0 text-[#6b7280]" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  resetPage();
                }}
                placeholder="Search batches..."
                className="w-full border-none bg-transparent text-sm text-[#111827] placeholder:text-[rgba(107,114,128,0.5)] focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <Select
                value={statusFilter}
                onValueChange={(v) => {
                  setStatusFilter(v);
                  resetPage();
                }}
              >
                <SelectTrigger className={SELECT_TRIGGER_CLASS}>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={trainerFilter}
                onValueChange={(v) => {
                  setTrainerFilter(v);
                  resetPage();
                }}
              >
                <SelectTrigger className={SELECT_TRIGGER_CLASS}>
                  <SelectValue placeholder="All Trainers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Trainers</SelectItem>
                  {trainerOptions.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table card */}
          <div className="pt-5">
            <OrgCard className="overflow-hidden">
              {pageItems.length === 0 ? (
                <div className="py-16">
                  <EmptyState icon={Search} title="No batches match your filters" description="Try adjusting your search or filters." />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] border-collapse">
                    <thead>
                      <tr className="border-b border-[#ececec]">
                        <th className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">Batch</th>
                        <th className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">Students</th>
                        <th className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">Trainer</th>
                        <th className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">Status</th>
                        <th className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">Progress</th>
                        <th className="px-6 py-4 text-left text-[12px] font-semibold uppercase tracking-[0.6px] text-[#9ca3af]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.map((b, i) => (
                        <BatchRow
                          key={b.id}
                          batch={b}
                          isLast={i === pageItems.length - 1}
                          onView={() => open(b.id)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination footer */}
              <div className="flex items-center justify-between border-t border-[#ececec] px-6 py-4">
                <p className="text-sm text-[#9ca3af]">
                  {filtered.length === 0
                    ? 'Showing 0 batches'
                    : `Showing ${from} to ${to} of ${filtered.length} batches`}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Previous page"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-[14px] border border-[#ececec] text-[#6b7280] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-[14px] w-[14px]" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => {
                    const n = i + 1;
                    const active = n === currentPage;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setPage(n)}
                        className={cn(
                          'flex h-[34px] w-[34px] items-center justify-center rounded-[14px] text-sm font-semibold',
                          active ? 'bg-[#111827] text-white' : 'border border-[#ececec] text-[#111827]',
                        )}
                      >
                        {n}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    aria-label="Next page"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="flex h-[34px] w-[34px] items-center justify-center rounded-[14px] border border-[#ececec] text-[#6b7280] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronRight className="h-[14px] w-[14px]" />
                  </button>
                </div>
              </div>
            </OrgCard>
          </div>

          {/* Summary strip */}
          {overview && (
            <div className="pt-5">
              <OrgCard className="flex overflow-hidden">
                <div className="flex-1 border-r border-[#ececec]">
                  <OrgStatTile icon={Layers} iconBg="#fff4f0" iconColor="#ff5a1f" value={overview.total_batches} label="Total Batches" />
                </div>
                <div className="flex-1 border-r border-[#ececec]">
                  <OrgStatTile icon={GraduationCap} iconBg="#f0fdfa" iconColor="#0f766e" value={overview.total_students} label="Total Students" />
                </div>
                <div className="flex-1 border-r border-[#ececec]">
                  <OrgStatTile icon={Users} iconBg="#fff4f0" iconColor="#ff5a1f" value={overview.total_trainers} label="Trainers" />
                </div>
                <div className="flex-1 border-r border-[#ececec]">
                  <OrgStatTile icon={CalendarCheck} iconBg="#f0fdf4" iconColor="#16a34a" value={overview.active_batches} label="Active Batches" />
                </div>
                <div className="flex-1">
                  <OrgStatTile icon={TrendingUp} iconBg="#eff6ff" iconColor="#2563eb" value={`${Math.round(overview.avg_completion_pct)}%`} label="Average Progress" />
                </div>
              </OrgCard>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BatchRow({
  batch,
  isLast,
  onView,
}: {
  batch: OrgBatchSummary;
  isLast: boolean;
  onView: () => void;
}) {
  const firstTrainer = batch.trainers[0];
  const extraTrainerCount = batch.trainers.length - 1;

  return (
    <tr className={cn(!isLast && 'border-b border-[#ececec]')}>
      <td className="px-6 py-5 align-top">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'h-[60px] w-[3px] flex-shrink-0 rounded-full',
              batch.status === 'active' ? 'bg-[#ff5a1f]' : 'bg-[#9ca3af]',
            )}
          />
          <div>
            <p className="text-[16px] font-semibold leading-6 text-[#111827]">{batch.name}</p>
            {batch.start_date && (
              <p className="pt-1 text-[12px] text-[#9ca3af]">
                Started on {format(new Date(batch.start_date), 'dd MMM yyyy')}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-5 align-top">
        <p className="text-[20px] font-bold leading-7 text-[#111827]">{batch.student_count}</p>
        <p className="text-[12px] text-[#9ca3af]">Students</p>
      </td>
      <td className="px-6 py-5 align-top">
        {firstTrainer ? (
          <div className="flex items-center gap-2">
            <OrgAvatar name={firstTrainer.name} />
            <div>
              <p className="text-[14px] font-medium text-[#111827]">{firstTrainer.name}</p>
              {extraTrainerCount > 0 && (
                <p className="text-[12px] text-[#9ca3af]">+{extraTrainerCount} more</p>
              )}
            </div>
          </div>
        ) : (
          <span className="text-sm text-[#9ca3af]">—</span>
        )}
      </td>
      <td className="px-6 py-5 align-top">
        <OrgStatusBadge status={batch.status} />
      </td>
      <td className="px-6 py-5 align-top">
        <p className="text-[24px] font-bold leading-8 text-[#111827]">{Math.round(batch.avg_completion_pct)}%</p>
        <OrgProgressBar
          pct={batch.avg_completion_pct}
          className="mt-1.5 h-1.5 w-[120px]"
          barClassName="bg-[#0f766e]"
        />
      </td>
      <td className="px-6 py-5 align-top">
        <button
          type="button"
          onClick={onView}
          className="flex items-center gap-1.5 rounded-[14px] border border-[#ececec] px-[13px] py-[9px] text-sm font-medium text-[#111827]"
        >
          View Batch
          <ArrowRight className="h-[13px] w-[13px]" />
        </button>
      </td>
    </tr>
  );
}
